import { useEffect, useRef, useCallback, useState, useContext } from "react";
import { useDispatch } from "react-redux";
import { useTradeInfo } from "@/redux/selectors/tradeInfoSelector";
import { useUI } from "@/redux/selectors/uiSelector";
import {
    ITradeChartAPI,
    TradeChartPriceType,
    TradeChartTimeframe,
    TradeChartType,
    initTradeChartTimeframe,
} from "@/hooks/chart/index";
import { getSymbolPrecision } from "@/hooks/chart/helpers";
import {
    getSymbolQuoteHistory,
    HistoryTimeframe,
    ISymbolQuoteHistoryResponse,
} from "@/services/trade/symbols";
import { IQuoteInfo, quoteSocketManager } from "@/hooks/trade/tradeSocket";
import { setSymbol } from "@/components/chart-tw/lib/dataUtils";
import { tickApi } from "@/components/chart-tw/lib/tickApi";
import { useChartStore } from "@/components/chart-tw/store/chartStore";
import { useTpSlStore } from "@/components/chart-tw/store/tpslStore";
import { TradeChartGlobalAPIContext } from "@/contexts/ChartAPI";
import { RefObject } from "react";

interface IProps {
    wrapRef?: RefObject<HTMLDivElement>;
    showLoader?: () => void;
    hideLoader?: () => void;
    chartType?: TradeChartType;
    symbolInfo?: any;
    isChartLoaded?: (value: boolean) => void;
}

// Map TradeChartTimeframe string to { apiTimeframe, minutes }
const timeframeMap: Record<string, { api: HistoryTimeframe; minutes: number }> = {
    "1m": { api: HistoryTimeframe.M1, minutes: 1 },
    "5m": { api: HistoryTimeframe.M5, minutes: 5 },
    "15m": { api: HistoryTimeframe.M15, minutes: 15 },
    "30m": { api: HistoryTimeframe.M30, minutes: 30 },
    "1h": { api: HistoryTimeframe.H1, minutes: 60 },
    "4h": { api: HistoryTimeframe.H4, minutes: 240 },
    "1d": { api: HistoryTimeframe.D1, minutes: 1440 },
    "1w": { api: HistoryTimeframe.W1, minutes: 10080 },
    "1MON": { api: HistoryTimeframe.MN1, minutes: 43200 },
};

// Map minutes back to TradeChartTimeframe string
const minutesToTimeframeStr: Record<number, string> = {
    1: "1m",
    5: "5m",
    15: "15m",
    30: "30m",
    60: "1h",
    240: "4h",
    1440: "1d",
    10080: "1w",
    43200: "1MON",
};

// Map TradeChartPriceType to adesk-graphs chart type
function mapPriceType(pt: TradeChartPriceType): "candle" | "bar" | "line" | "area" {
    switch (pt) {
        case TradeChartPriceType.Candlesticks:
        case TradeChartPriceType.HollowCandlesticks:
        case TradeChartPriceType.Heikinashi:
            return "candle";
        case TradeChartPriceType.OHLC:
        case TradeChartPriceType.HLC:
            return "bar";
        case TradeChartPriceType.Line:
        case TradeChartPriceType.Dots:
            return "line";
        case TradeChartPriceType.Area:
        case TradeChartPriceType.Histogram:
            return "area";
        default:
            return "candle";
    }
}

const useTradeChartTW = (props: IProps = {}): ITradeChartAPI => {
    const {
        showLoader = () => {},
        hideLoader = () => {},
        isChartLoaded = () => {},
    } = props;

    const dispatch = useDispatch();
    const { activeSymbolId, symbolsInfoById } = useTradeInfo();
    const symbolInfo = props.symbolInfo || symbolsInfoById[activeSymbolId];
    const { isChartReady } = useUI();
    const { updateGlobalChartAPI } = useContext(TradeChartGlobalAPIContext);

    const [isLoaded, setIsLoaded] = useState(false);
    const socketHandlerRef = useRef<((quote: IQuoteInfo) => void) | null>(null);
    const subscribedSymbolRef = useRef<string | null>(null);
    const currentTimeframeRef = useRef<string>(initTradeChartTimeframe);
    const abortRef = useRef<AbortController | null>(null);

    // Stabilize callback deps via refs
    const showLoaderRef = useRef(showLoader);
    const hideLoaderRef = useRef(hideLoader);
    const symbolPointRef = useRef(symbolInfo?.point);
    showLoaderRef.current = showLoader;
    hideLoaderRef.current = hideLoader;
    symbolPointRef.current = symbolInfo?.point;

    // Set the symbol for localStorage-based stores
    useEffect(() => {
        if (activeSymbolId) {
            setSymbol(activeSymbolId);
        }
    }, [activeSymbolId]);

    // Fetch historical data and push to chart via tickApi
    const fetchAndPushHistory = useCallback(
        async (symbolId: string, timeframeStr: string) => {
            const tfInfo = timeframeMap[timeframeStr];
            if (!tfInfo) return;

            // Cancel any in-flight request
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;

            showLoaderRef.current();

            try {
                const response = (await getSymbolQuoteHistory({
                    symbol: symbolId,
                    timeframe: tfInfo.api,
                    signal: controller.signal,
                })) as ISymbolQuoteHistoryResponse;

                if (!response?.quoteHistory?.data || response?.isFailed) {
                    hideLoaderRef.current();
                    return;
                }

                const candles = response.quoteHistory.data
                    .filter(({ open, close }) => open > 0 && close > 0)
                    .map(entry => ({
                        time: Math.floor(new Date(entry.time).getTime() / 1000),
                        open: entry.open,
                        high: entry.high,
                        low: entry.low,
                        close: entry.close,
                        volume: entry.volume || 0,
                    }));

                tickApi.pushCandles(candles);
                hideLoaderRef.current();
            } catch (e: any) {
                if (e?.name === 'AbortError') return;
                hideLoaderRef.current();
            }
        },
        []
    );

    // Subscribe to real-time socket updates
    const subscribeToSocket = useCallback(
        (symbolId: string) => {
            // Unsubscribe from previous
            if (subscribedSymbolRef.current && socketHandlerRef.current) {
                quoteSocketManager.unsubscribe(subscribedSymbolRef.current, socketHandlerRef.current);
            }

            subscribedSymbolRef.current = symbolId;

            socketHandlerRef.current = (quote: IQuoteInfo) => {
                // Guard: only process ticks for the currently subscribed symbol
                if (subscribedSymbolRef.current !== symbolId) return;

                const { ask, bid } = quote;
                const midPrice = bid;
                const precision = getSymbolPrecision(symbolPointRef.current || 0.00001);

                useChartStore.getState().setBidAsk(bid.toFixed(precision), ask.toFixed(precision));

                tickApi.pushTick({
                    price: midPrice,
                    volume: 0,
                    time: Math.floor(Date.now() / 1000),
                });
            };

            quoteSocketManager.subscribe(dispatch, symbolId, socketHandlerRef.current);
        },
        [dispatch]
    );

    // On mount / symbol change / chart ready: load data + subscribe
    useEffect(() => {
        if (!isChartReady || !activeSymbolId) return;

        // Reset chart store immediately to prevent showing stale data from previous symbol
        useChartStore.getState().setLivePrice(0);
        useChartStore.getState().setBidAsk('\u2014', '\u2014');

        fetchAndPushHistory(activeSymbolId, currentTimeframeRef.current);
        subscribeToSocket(activeSymbolId);

        setIsLoaded(true);

        return () => {
            abortRef.current?.abort();
            if (subscribedSymbolRef.current && socketHandlerRef.current) {
                quoteSocketManager.unsubscribe(subscribedSymbolRef.current, socketHandlerRef.current);
                socketHandlerRef.current = null;
                subscribedSymbolRef.current = null;
            }
        };
    }, [activeSymbolId, isChartReady, fetchAndPushHistory, subscribeToSocket]);

    useEffect(() => {
        isChartLoaded(isLoaded);
    }, [isLoaded]);

    // Listen to Zustand timeframe changes from ChartBar clicks
    useEffect(() => {
        const unsub = useChartStore.subscribe((state, prevState) => {
            if (state.timeframe !== prevState.timeframe) {
                const tfStr = minutesToTimeframeStr[state.timeframe] || "5m";
                currentTimeframeRef.current = tfStr;
                if (activeSymbolId) {
                    fetchAndPushHistory(activeSymbolId, tfStr);
                }
            }
        });
        return unsub;
    }, [activeSymbolId]);

    // API methods
    const changeTimeframe = useCallback(
        (t: TradeChartTimeframe) => {
            const tfInfo = timeframeMap[t];
            if (tfInfo) {
                currentTimeframeRef.current = t;
                useChartStore.getState().setTimeframe(tfInfo.minutes);
            }
        },
        []
    );

    const changePriceType = useCallback((p: TradeChartPriceType) => {
        useChartStore.getState().setChartType(mapPriceType(p));
    }, []);

    const addStopLossLine = useCallback((price: number) => {
        useTpSlStore.getState().setSl(price);
    }, []);

    const removeStopLossLine = useCallback(() => {
        useTpSlStore.getState().setSl(null);
    }, []);

    const addTakeProfitLine = useCallback((price: number) => {
        useTpSlStore.getState().setTp(price);
    }, []);

    const removeTakeProfitLine = useCallback(() => {
        useTpSlStore.getState().setTp(null);
    }, []);

    // Update global chart API so the rest of the app can use it
    useEffect(() => {
        if (isLoaded) {
            updateGlobalChartAPI({
                changePriceType,
                changeTimeframe,
                addStopLossLine,
                removeStopLossLine,
                addTakeProfitLine,
                removeTakeProfitLine,
            });
        }
    }, [isLoaded]);

    return {
        changePriceType,
        changeTimeframe,
        addStopLossLine,
        removeStopLossLine,
        addTakeProfitLine,
        removeTakeProfitLine,
    };
};

export default useTradeChartTW;
